import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import useClickAwayListener from '@/utils/use-click-away-listener';
import useOnFocusAny from '@/utils/use-on-focus-any';
import EditTextWrapper from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/edit-text-wrapper';
import { EditStateWrapperProps } from './edit-state-wrapper.types';

import styles from './edit-state-wrapper.module.css';

const EditStateWrapper = ({
  disabled,
  children,
  Label,
  FocusedLabel,
  onFocusedWithinChange,
  onHeaderClick,
  onContainerClick,
  focusSelectorOnOpen,
  focusSelectorOnHeaderClick,
  onEscape,
  onFocusAny,
  className,
  focusedClassName,
  hideEditLabel,
  hideHeader,
  hideHeaderWhenFocused,
  editLabelClassName,
  keepOpen,
  headerClassName,
}: EditStateWrapperProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [tabbedIn, setTabbedIn] = useState(!!keepOpen);

  useEffect(() => {
    if (keepOpen) setTabbedIn(true);
  }, [keepOpen]);

  useEffect(() => {
    onFocusedWithinChange?.(tabbedIn);
  }, [onFocusedWithinChange, tabbedIn]);

  const handleFocusSelector = useCallback(() => {
    const containerEl = containerRef?.current;
    if (!containerEl) return;
    if (!focusSelectorOnOpen) return;
    const elToFocus = containerEl.querySelector(focusSelectorOnOpen);
    if (elToFocus && elToFocus instanceof HTMLElement) {
      elToFocus.focus();
    }
  }, [focusSelectorOnOpen]);

  const setTabbedInHelper = useCallback(
    ({ isTabbedIn, type }: { isTabbedIn: boolean; type: 'mouse' | 'keyboard' | 'any' }) => {
      if (keepOpen) return;
      const shouldBeTabbedIn = disabled ? false : isTabbedIn;
      setTabbedIn(shouldBeTabbedIn);

      // let the component re-render, then try and focus
      if (!tabbedIn && shouldBeTabbedIn && type === 'mouse') {
        setTimeout(() => {
          handleFocusSelector();
        }, 0);
      }
    },
    [disabled, handleFocusSelector, keepOpen, tabbedIn]
  );

  useClickAwayListener({
    ref: containerRef,
    open: true,
    resetFocusedOnEscape: false,
    onTabAway: () => {
      setTabbedInHelper({ isTabbedIn: false, type: 'keyboard' });
    },
    onPointerDownInside: () => {
      setTabbedInHelper({ isTabbedIn: true, type: 'mouse' });
    },
    onPointerUpInside: () => {
      setTabbedInHelper({ isTabbedIn: true, type: 'mouse' });
    },
    onPointerDownOutside: () => {
      setTabbedInHelper({ isTabbedIn: false, type: 'mouse' });
    },
    onPointerUpOutside: () => {
      setTabbedInHelper({ isTabbedIn: false, type: 'mouse' });
    },
    onEscape,
  });

  useOnFocusAny({
    ref: containerRef,
    onFocus: (e) => {
      setTabbedInHelper({ isTabbedIn: true, type: 'any' });
      onFocusAny?.(e);
    },
  });

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      onHeaderClick?.(e);

      if (focusSelectorOnHeaderClick) {
        setTimeout(() => {
          handleFocusSelector();
        }, 0);
      }
    },
    [disabled, focusSelectorOnHeaderClick, handleFocusSelector, onHeaderClick]
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (!disabled && onContainerClick) {
        onContainerClick(e);
      }
    },
    [disabled, onContainerClick]
  );

  return (
    <div
      className={classNames(styles['edit-state-wrapper'], className || '', {
        [styles.isFocused]: tabbedIn,
        [focusedClassName || '']: tabbedIn,
      })}
      ref={containerRef}
      // 	note: intentional onClick on a div - we don't want this to be tabbable because the select/input components themselves is tabbable
      onClick={handleContainerClick}
      data-component="EditStateWrapper"
    >
      {hideHeader || (hideHeaderWhenFocused && tabbedIn) ? null : (
        <div
          // note: intentional onClick on a div - we don't want this to be tabbable because the select/input components themselves is tabbable
          onClick={handleHeaderClick}
          className={classNames(
            `flex ai-center`,
            {
              'jc-space-between': !!Label,
              'jc-flex-end': !Label,
            },
            headerClassName || ''
          )}
        >
          {(!FocusedLabel && Label) || (FocusedLabel && Label && !tabbedIn) ? (
            <>
              {typeof Label === 'function' ? (
                <Label isFocusedWithin={tabbedIn} setIsFocusedWithin={setTabbedIn} containerRef={containerRef} />
              ) : null}

              {typeof Label === 'string' ? (
                <Container width="100%" className="flex-1">
                  <Text className="L3 padding-b-8" design="GlobalIcoText1">
                    {Label}
                  </Text>
                </Container>
              ) : null}
            </>
          ) : null}

          {FocusedLabel && tabbedIn ? (
            <>
              {typeof FocusedLabel === 'function' ? (
                <FocusedLabel isFocusedWithin={tabbedIn} setIsFocusedWithin={setTabbedIn} containerRef={containerRef} />
              ) : null}

              {typeof FocusedLabel === 'string' ? (
                <Container width="100%" className="flex-1">
                  <Text className="L3 padding-b-8" design="GlobalIcoText1">
                    {FocusedLabel}
                  </Text>
                </Container>
              ) : null}
            </>
          ) : null}

          <OptionalComponent display={!disabled && !hideEditLabel}>
            <div
              className={`${styles['edit-state-wrapper__edit-text-wrapper']} padding-b-8 z-1 ${editLabelClassName || ''}`}
            >
              <EditTextWrapper />
            </div>
          </OptionalComponent>
        </div>
      )}

      <Container className={`flex fd-row jc-flex-start ai-center radius-8 ${styles['edit-state-wrapper-container']}`}>
        {typeof children === 'function'
          ? children({ isFocusedWithin: tabbedIn, setIsFocusedWithin: setTabbedIn, containerRef })
          : children}
      </Container>
    </div>
  );
};

export default EditStateWrapper;
